import type { HallOfFameEntry } from "./career.js";

/**
 * El salon de la fama: todos los que colgaron las botas en este navegador.
 * Sobrevive a las carreras (esa es la gracia) y se lee del mas nuevo al mas
 * viejo. Con tope: un salon con mil bustos ya no es un salon, es un almacen.
 */
export const HALL_KEY = "fynta.hallOfFame";
export const HALL_LIMIT = 50;

/** Quien ha cambiado el salon: un retiro aqui, o bustos que llegaron de la cuenta. */
const listeners = new Set<(source: "local" | "cloud") => void>();

export function onHallChange(fn: (source: "local" | "cloud") => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function isEntry(raw: unknown): raw is HallOfFameEntry {
  if (!raw || typeof raw !== "object") return false;
  const e = raw as Partial<HallOfFameEntry>;
  return (
    typeof e.id === "string" &&
    typeof e.name === "string" &&
    typeof e.seasons === "number" &&
    typeof e.verdict === "string" &&
    Array.isArray(e.titles) &&
    Array.isArray(e.clubs)
  );
}

export function loadHallOfFame(storage: StorageLike | null = safeLocalStorage()): HallOfFameEntry[] {
  try {
    const raw = storage?.getItem(HALL_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

/** Añade (o reemplaza, si ya estaba) y devuelve el salon entero, del mas nuevo al mas viejo. */
export function addToHallOfFame(
  entry: HallOfFameEntry,
  storage: StorageLike | null = safeLocalStorage(),
): HallOfFameEntry[] {
  const hall = [entry, ...loadHallOfFame(storage).filter((e) => e.id !== entry.id)].slice(0, HALL_LIMIT);
  write(hall, storage, "local");
  return hall;
}

/** El salon entero de una vez (lo que queda al juntar el de aqui con el de la cuenta). */
export function replaceHallOfFame(
  hall: readonly HallOfFameEntry[],
  storage: StorageLike | null = safeLocalStorage(),
): void {
  write([...hall].sort((a, b) => b.retiredAt - a.retiredAt).slice(0, HALL_LIMIT), storage, "cloud");
}

function write(hall: HallOfFameEntry[], storage: StorageLike | null, source: "local" | "cloud"): void {
  try {
    storage?.setItem(HALL_KEY, JSON.stringify(hall));
  } catch {
    // Sin sitio: el busto dura lo que la pestaña.
  }
  for (const fn of listeners) fn(source);
}
